import { MaskedPhoneNumberInputSchema } from '@aspendental/shared-forms-web';
import { differenceInYears, isValid, parse } from 'date-fns';
import z from 'zod';

const DATE_FORMAT = 'MM/dd/yyyy';

interface translationMappings {
	firstNameRequired: string;
	max30Chars: string;
	lastNameRequired: string;
	validEmail: string;
	dateOfBirthRequired: string;
	minYear: string;
	patientMinAge: string;
	phoneRequired: string;
}

export const personalInfoForm = {
	firstName: {
		name: 'firstName',
		label: 'First Name',
		labelKey: 'firstName',
		schema: z
			.string()
			.trim()
			.min(1, 'Valid first name required')
			.regex(/^[a-zA-Z]+([ '-][a-zA-Z]+)*$/, 'Valid first name required')
			.refine((val) => {
				return (
					!val.includes('  ') && // No double spaces
					!val.startsWith(' ') && // No leading spaces (should be handled by trim)
					!val.endsWith(' ') && // No trailing spaces (should be handled by trim)
					!val.startsWith('-') && // No leading hyphens
					!val.endsWith('-') && // No trailing hyphens
					!val.startsWith("'") && // No leading apostrophes
					!val.endsWith("'") // No trailing apostrophes
				);
			}, 'Valid first name required'),
	},
	lastName: {
		name: 'lastName',
		label: 'Last Name',
		labelKey: 'lastName',
		schema: z
			.string()
			.trim()
			.min(1, 'Valid last name required')
			.regex(/^[a-zA-Z]+([ '-][a-zA-Z]+)*$/, 'Valid last name required')
			.refine((val) => {
				return (
					!val.includes('  ') && // No double spaces
					!val.startsWith(' ') && // No leading spaces (should be handled by trim)
					!val.endsWith(' ') && // No trailing spaces (should be handled by trim)
					!val.startsWith('-') && // No leading hyphens
					!val.endsWith('-') && // No trailing hyphens
					!val.startsWith("'") && // No leading apostrophes
					!val.endsWith("'") // No trailing apostrophes
				);
			}, 'Valid last name required'),
	},
	email: {
		name: 'email',
		label: 'Email',
		labelKey: 'email',
		schema: z
			.string()
			.trim()
			.regex(/^[^,]+$/, 'Please enter a valid email address')
			.refine((val) => {
				// Ensure the email contains exactly one '@'
				const atIndex = val.indexOf('@');
				if (atIndex === -1 || val.indexOf('@', atIndex + 1) !== -1) {
					return false;
				}

				// Split the email into local part and domain part
				const [localPart, domainPart] = val.split('@');

				// Ensure local and domain parts exist
				if (!localPart || !domainPart) {
					return false;
				}

				// Ensure the email does not start with a dot
				if (val.startsWith('.')) {
					return false;
				}

				// Ensure no spaces, commas, or consecutive dots
				if (val.includes(' ') || val.includes(',') || val.includes('..')) {
					return false;
				}

				// Ensure the domain part contains at least one dot
				if (!domainPart.includes('.') || domainPart.startsWith('.') || domainPart.endsWith('.')) {
					return false;
				}

				return true;
			}, 'Valid email address required'),
	},
	phone: {
		name: 'phone',
		label: 'Phone',
		labelKey: 'phone',
		schema: MaskedPhoneNumberInputSchema.options.default,
	},
	dateOfBirth: {
		name: 'dateOfBirth',
		label: 'Date of Birth',
		labelKey: 'dateOfBirth',
		schema: z
			.string()
			.refine(
				(val) => {
					const parsedDate = parse(val, DATE_FORMAT, new Date());
					return isValid(parsedDate);
				},
				{
					message: 'Valid Date of Birth is required',
				}
			)
			.refine(
				(val) => {
					const [_month, _day, year] = val.split('/').map(Number);
					return year > 1900;
				},
				{
					message: 'Year must be greater than 1900',
				}
			)
			.transform((val) => {
				const [month, day, year] = val.split('/').map(Number);
				return new Date(year, month - 1, day);
			})
			.refine(
				(val) => {
					const yearsSinceDate = differenceInYears(new Date(), val);
					return yearsSinceDate >= 18;
				},
				{
					message: 'Patient must be 18 or over',
				}
			),
	},
};

const PhoneSchemaWithTranslation = (invalidPhoneMessage: string) =>
	z
		.string()
		.trim()
		.refine(
			(val) => {
				return /^\(\d{3}\) \d{3}-\d{4}$/.test(val);
			},
			{ message: invalidPhoneMessage }
		);

export const personalInfoFormSchema = [
	{ name: personalInfoForm.firstName.name, validation: personalInfoForm.firstName.schema },
	{ name: personalInfoForm.lastName.name, validation: personalInfoForm.lastName.schema },
	{ name: personalInfoForm.email.name, validation: personalInfoForm.email.schema },
	{ name: personalInfoForm.phone.name, validation: personalInfoForm.phone.schema },
	{ name: personalInfoForm.dateOfBirth.name, validation: personalInfoForm.dateOfBirth.schema },
];

export default function generatePersonalInfoFormSchema(translations: translationMappings) {
	const personalInfoFormConfiguration = {
		firstName: {
			name: 'firstName',
			label: 'First Name',
			labelKey: 'firstName',
			schema: z
				.string()
				.trim()
				.min(1, 'Valid first name required')
				.regex(/^[a-zA-Z]+([ '-][a-zA-Z]+)*$/, 'Valid first name required')
				.refine((val) => {
					return (
						!val.includes('  ') && // No double spaces
						!val.startsWith(' ') && // No leading spaces
						!val.endsWith(' ') && // No trailing spaces
						!val.startsWith('-') && // No leading hyphens
						!val.endsWith('-') && // No trailing hyphens
						!val.startsWith("'") && // No leading apostrophes
						!val.endsWith("'") // No trailing apostrophes
					);
				}, 'Valid first name required'),
		},
		lastName: {
			name: 'lastName',
			label: 'Last Name',
			labelKey: 'lastName',
			schema: z
				.string()
				.trim()
				.min(1, 'Valid last name required')
				.regex(/^[a-zA-Z]+([ '-][a-zA-Z]+)*$/, 'Valid last name required')
				.refine((val) => {
					return (
						!val.includes('  ') && // No double spaces
						!val.startsWith(' ') && // No leading spaces
						!val.endsWith(' ') && // No trailing spaces
						!val.startsWith('-') && // No leading hyphens
						!val.endsWith('-') && // No trailing hyphens
						!val.startsWith("'") && // No leading apostrophes
						!val.endsWith("'") // No trailing apostrophes
					);
				}, 'Valid last name required'),
		},
		email: {
			name: 'email',
			label: 'Email',
			labelKey: 'email',
			schema: z
				.string()
				.trim()
				.regex(/^[^,]+$/, translations.validEmail)
				.refine((val) => {
					// Ensure the email contains exactly one '@'
					const atIndex = val.indexOf('@');
					if (atIndex === -1 || val.indexOf('@', atIndex + 1) !== -1) {
						return false;
					}

					// Split the email into local part and domain part
					const [localPart, domainPart] = val.split('@');

					// Ensure local and domain parts exist
					if (!localPart || !domainPart) {
						return false;
					}

					// Ensure the email does not start with a dot
					if (val.startsWith('.')) {
						return false;
					}

					// Ensure no spaces, commas, or consecutive dots
					if (val.includes(' ') || val.includes(',') || val.includes('..')) {
						return false;
					}

					// Ensure the domain part contains at least one dot
					if (!domainPart.includes('.') || domainPart.startsWith('.') || domainPart.endsWith('.')) {
						return false;
					}

					return true;
				}, translations.validEmail),
		},
		phone: {
			name: 'phone',
			label: 'Phone',
			labelKey: 'phone',
			// TODO: look into configuring translation for this phone num
			schema: PhoneSchemaWithTranslation(translations.phoneRequired),
		},
		dateOfBirth: {
			name: 'dateOfBirth',
			label: 'Date of Birth',
			labelKey: 'dateOfBirth',
			schema: z
				.string()
				.refine(
					(val) => {
						const parsedDate = parse(val, DATE_FORMAT, new Date());
						return isValid(parsedDate);
					},
					{
						message: translations.dateOfBirthRequired,
					}
				)
				.refine(
					(val) => {
						const [_month, _day, year] = val.split('/').map(Number);
						return year > 1900;
					},
					{
						message: translations.minYear,
					}
				)
				.transform((val) => {
					const [month, day, year] = val.split('/').map(Number);
					return new Date(year, month - 1, day);
				})
				.refine(
					(val) => {
						const yearsSinceDate = differenceInYears(new Date(), val);
						return yearsSinceDate >= 18;
					},
					{
						message: translations.patientMinAge,
					}
				),
		},
	};

	return [
		{
			name: personalInfoFormConfiguration.firstName.name,
			validation: personalInfoFormConfiguration.firstName.schema,
		},
		{
			name: personalInfoFormConfiguration.lastName.name,
			validation: personalInfoFormConfiguration.lastName.schema,
		},
		{ name: personalInfoFormConfiguration.email.name, validation: personalInfoFormConfiguration.email.schema },
		{ name: personalInfoFormConfiguration.phone.name, validation: personalInfoFormConfiguration.phone.schema },
		{
			name: personalInfoFormConfiguration.dateOfBirth.name,
			validation: personalInfoFormConfiguration.dateOfBirth.schema,
		},
	];
}
